'use client'
import { memo, useMemo } from 'react'
import { useParams, usePathname } from 'next/navigation'
import dynamic from 'next/dynamic'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import { capitalizeFirstLetter, CATALOG_TRANSLATION, LANG, SEARCHER_TRANSLATION } from '@/utils'
import { mq, vw } from '@/styles'
import type { TypeCmsCategory } from '@/schema'

const DynamicMarquee = dynamic(() => import('./layout/Marquee').then((mod) => mod.Marquee), { ssr: false })

type TypeHeaderMarquee = {
  data?: string | TypeCmsCategory[]
  isCart?: boolean
  className?: string
}

const ORDER_TRANSLATION = {
  en: 'Order',
  es: 'Pedido',
  fr: 'Commande',
  de: 'Bestellung',
  it: 'Ordine',
  pt: 'Encomenda'
}

const getCategoryNameByPathname = (categories:TypeCmsCategory[], pathname: string):string => {
  const category = categories.find(cat => pathname.includes(cat.slug?.[LANG]))

  if (category) return category.name as string

  for (const cat of categories) {
    if (cat.subcategories) {
      const subcategory = cat.subcategories.find((subcat) =>
        pathname.includes(subcat.slug[LANG])
      )
      if (subcategory) {
        return subcategory.name as string
      }
    }
  }

  if (pathname.includes('search'))
    return SEARCHER_TRANSLATION[LANG] || 'Searcher'

  return CATALOG_TRANSLATION[LANG] || 'Catalog'
}

const getTitle = (pathname, data, isCart, slug, t) => {
  if (isCart) {
    return t('project.nav_menu.cart.name') as string
  }
  if (!data) {
    if (pathname.includes('my-products')) {
      return t('project.my_products.my_usual') as string
    } else if (pathname === '/account/order') {
      return ORDER_TRANSLATION[LANG] || 'Order'
    } else {
      const navAccount = t('project.nav_account')
      const navArray = Array.isArray(navAccount)
        ? (navAccount as unknown as {
          name: string;
          pathname: string;
          slug?: string;
        }[])
        : []
      const currentPage = navArray.find((item) => item.slug === slug)
      return currentPage?.name
    }
  } else {
    if (typeof data !== 'string') {
      const name = getCategoryNameByPathname(data, pathname)
      return name
    } else {
      return capitalizeFirstLetter(data)
    }
  }
}

export const HeaderMarquee = memo<TypeHeaderMarquee>(({ data, className, isCart = false }) => {
  const { t } = useTranslator()
  const { slug } = useParams()
  const pathname = usePathname()
  const title = useMemo(() => getTitle(pathname, data, isCart, slug, t), [pathname, data, isCart, slug, t])

  return (
    <Header {...(className && { className })}>
      {(!pathname.includes('catalog/product') && !pathname.includes('catalog/brand')) && <h1 className='srt-only'>{title}</h1>}
      <DynamicMarquee data={title} color='--color-gold-background' />
    </Header>
  )
})

const Header = styled.header<{ $isEmptyHeader?: boolean }>`
  min-height: ${vw(60, 'mobile')};
  margin: ${({ $isEmptyHeader }) => $isEmptyHeader ? '0' : '15px 0 30px'};
  overflow: hidden;
  width: 100vw;

  ${mq.greaterThan('tablet')} {
    min-height: 100px;
    margin: ${({ $isEmptyHeader }) => $isEmptyHeader ? '0' : '25px 0 40px'};
  }

  .marquee {
    background-color: unset;
    padding: 0;
  }
`
